import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCustomMutation,
  useHandleNotification,
  useInvalidate,
} from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import { FieldValues, FormProvider } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "../../../hooks/useTranslation";
import { CountryList } from "./CountryList";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";

type AddRestrictionDialogProps = {
  open: boolean;
  onClose: () => void;
  endpoint: string;
  translationPrefix: string;
  hasDescription?: boolean;
  onShowResult: (_data: AddResponse) => void;
};

type RestrictionReport = {
  marketplaceId: number;
  countOfExistingRestrictions: number;
  existingRestrictions: string[];
};

export type AddResponse = {
  totalKeywords: number;
  countOfDuplicateKeywords: number;
  duplicateKeywords: string[];
  incorrectKeywords: string[] | undefined;
  restrictionsSavingReports: RestrictionReport[];
};

export type AddFormValues = {
  restrictions: string;
  marketplaceIds: number[];
  applyToAllMarketplaces: boolean;
  note?: string;
};

export const AddRestrictionDialog = (props: AddRestrictionDialogProps) => {
  const {
    open,
    onClose,
    endpoint,
    hasDescription,
    translationPrefix,
    onShowResult,
  } = props;

  const countries = useMarketplaceStore((state) => state.countries);
  const { translate, translateWithoutPrefix } =
    useTranslation(translationPrefix);

  const methods = useForm<AddFormValues>({
    defaultValues: {
      marketplaceIds: [],
      applyToAllMarketplaces: false,
    },
    warnWhenUnsavedChanges: false,
  });

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const handleNotification = useHandleNotification();
  const { mutate, isLoading } = useCustomMutation();
  const invalidate = useInvalidate();

  const onSubmit = (data: FieldValues) => {
    const { restrictions, applyToAllMarketplaces, marketplaceIds, ...rest } =
      data;

    const marketplaceIdsToSend = applyToAllMarketplaces
      ? countries.map((c) => c.id)
      : marketplaceIds;

    mutate(
      {
        url: endpoint,
        method: "post",
        values: {
          keywords: restrictions.split("\n"),
          marketplaceIds: marketplaceIdsToSend,
          ...rest,
        },
      },
      {
        onSuccess: (data) => {
          const addResponse = data.data as AddResponse;

          const anyMarketHasDuplication =
            addResponse.restrictionsSavingReports?.some(
              (r) => r.countOfExistingRestrictions > 0,
            );

          const requestHasDuplication =
            addResponse.countOfDuplicateKeywords > 0;

          const requestHasIncorrectKeywords =
            addResponse.incorrectKeywords &&
            addResponse.incorrectKeywords.length > 0;

          if (
            anyMarketHasDuplication ||
            requestHasDuplication ||
            requestHasIncorrectKeywords
          ) {
            onShowResult(addResponse);
          } else {
            handleNotification({
              message: translate("saveMessage"),
              type: "success",
            });
          }

          invalidate({
            resource: "all",
            invalidates: ["all"],
          });
          handleClose();
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const getErrorMessage = () => {
    if (errors.restrictions?.type === "required") {
      return translate("listError");
    } else if (errors.restrictions?.type === "validate") {
      return translate("listLengthError");
    } else {
      return "";
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={isLoading ? undefined : handleClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit(onSubmit),
        }}
      >
        <DialogTitle>{translate("title")}</DialogTitle>
        <DialogContent>
          <Alert sx={{ marginBottom: "8px" }} severity="info">
            <Typography variant="body2">{translate("description")}</Typography>
          </Alert>
          <TextField
            autoFocus
            placeholder={translate("listHint")}
            fullWidth
            multiline
            variant="outlined"
            error={!!errors.restrictions}
            helperText={getErrorMessage()}
            maxRows={8}
            {...register("restrictions", {
              required: true,
              validate: (v) => v.split("\n").length <= 5000,
            })}
          />
          {hasDescription ? (
            <TextField
              size="small"
              sx={{ margin: "8px 0" }}
              placeholder={translate("descriptionHint")}
              fullWidth
              variant="outlined"
              {...register("note")}
            />
          ) : (
            <Box margin="8px 0" />
          )}
          <CountryList />
        </DialogContent>
        <DialogActions>
          <Button disabled={isLoading} onClick={handleClose}>
            {translateWithoutPrefix("buttons.cancel")}
          </Button>
          <LoadingButton type="submit" loading={isLoading}>
            {translateWithoutPrefix("buttons.save")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
